import Image from 'next/image';

import useToggle from '@lyearn/core/hooks/useToggle';
import {
  ExcalamationInSquareOutline,
  FourSquaresOutline,
  GlobeOutline,
  ImageOutline,
  SparkleOutline,
} from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';

import EmptyState from '../../../KnowledgeAssetDetails/components/EmptyState';
import ImageViewerModal from '../../components/ImageViewerModal';

interface BusinessImpactsProps {
  painSolved: string[];
  productsIncluded: string[];
  aiCapabilities: string[];
  predictedTime: string;
  actualTime: string;
  blueprintImages: string[];
  cxLinks: string[];
}

const BusinessImpacts = ({
  painSolved,
  productsIncluded,
  aiCapabilities,
  predictedTime,
  actualTime,
  blueprintImages,
  cxLinks,
}: BusinessImpactsProps) => {
  const [isImageViewerOpen, { on, off }] = useToggle(false);

  return (
    <Stack className="w-full xs:min-w-full xs:p-12 xs:mt-12" direction="col" gapY={32}>
      <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
        {/* Pain Points Section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <ExcalamationInSquareOutline fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-xs">Pain Points</Typography>
          </Stack>
          <Stack direction="col" gapY={12}>
            {painSolved.length > 0 ? (
              painSolved.map((point, index) => (
                <Stack key={index} direction="col" gapY={12}>
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {point}
                  </Typography>
                  {index !== painSolved.length - 1 && <div className="border-b border-border" />}
                </Stack>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>

        {/* AI Capability Section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <SparkleOutline fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-xs">AI Capability</Typography>
          </Stack>
          <Stack direction="col" gapY={12}>
            {aiCapabilities.length > 0 ? (
              aiCapabilities.map((capability, index) => (
                <Stack key={index} direction="col" gapY={12}>
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {capability}
                  </Typography>
                  {index !== aiCapabilities.length - 1 && (
                    <div className="border-b border-border" />
                  )}
                </Stack>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>

        {/* Products Included Section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <FourSquaresOutline fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-xs">Products Included</Typography>
          </Stack>
          <Stack direction="col" gapY={12}>
            {productsIncluded.length > 0 ? (
              productsIncluded.map((product, index) => (
                <Stack key={index} direction="col" gapY={12}>
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {product}
                  </Typography>
                  {index !== productsIncluded.length - 1 && (
                    <div className="border-b border-border" />
                  )}
                </Stack>
              ))
            ) : (
              <EmptyState />
            )}
          </Stack>
        </Stack>

        {/* Blueprint Images section */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" direction="row" gapX={12} justifyContent="between">
            <Stack alignItems="center" gapX={8}>
              <ImageOutline fillColorToken="icon-active" height={24} width={24} />
              <Typography variant="heading-xs">Blueprints</Typography>
            </Stack>
            {blueprintImages.length > 1 && (
              <Typography
                className="cursor-pointer text-text-link hover:underline ml-auto"
                variant="body-short-02"
                onClick={on}>
                View all
              </Typography>
            )}
          </Stack>
          {blueprintImages.length > 0 ? (
            <div className="relative w-full h-[450px]">
              <Image
                fill
                alt="Journey"
                className="rounded-12 bg-bg-secondary"
                src={blueprintImages[0]}
                style={{ objectFit: 'contain' }}
              />
            </div>
          ) : (
            <EmptyState height="h-full min-h-[100px]" />
          )}
        </Stack>
        <ImageViewerModal images={blueprintImages} isOpen={isImageViewerOpen} onClose={off} />

        {/* URLs */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <GlobeOutline fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-xs">URLs</Typography>
          </Stack>
          <Stack className="flex-1 mt-24 overflow-hidden" direction="col">
            {(cxLinks?.length ?? 0) > 0 ? (
              cxLinks.map((link, index) => (
                <Stack
                  key={index}
                  className={cn(
                    'cursor-pointer group w-full',
                    index === cxLinks.length - 1 ? '' : 'border-b border-border pb-12 mb-12',
                  )}
                  onClick={() => window.open(link, '_blank')}>
                  <Typography
                    key={index}
                    className="text-link-external group-hover:text-link-external-hover"
                    variant="body-long-01">
                    {link}
                  </Typography>
                </Stack>
              ))
            ) : (
              <EmptyState height="h-full min-h-[100px]" />
            )}
          </Stack>
        </Stack>

        {/* Predicted Time To Vale Section */}
        {/* <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack alignItems="center" gapX={8}>
            <GlobeFill fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-xs">Predicted Time To Vale</Typography>
          </Stack>
          <Stack direction="col" gapY={24}>
            <Stack direction="col" gapY={4}>
              <Typography variant="label-01">Predicted Time</Typography>
              <Typography className="text-text-secondary" variant="body-long-01">
                {predictedTime || 'N/A'}
              </Typography>
            </Stack>
            <Stack direction="col" gapY={4}>
              <Typography variant="label-01">Actual Time</Typography>
              <Typography className="text-text-secondary" variant="body-long-01">
                {actualTime || 'N/A'}
              </Typography>
            </Stack>
          </Stack>
        </Stack> */}
      </Stack>
    </Stack>
  );
};

export default BusinessImpacts;
